import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Rocket } from "lucide-react";

// Logo #07. the "A" rising from a flowing field of data bits.
export default function IntroOverlay() {
  const canvasRef = useRef(null);
  const [count, setCount] = useState(5);

  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let W = canvas.clientWidth;
    let H = canvas.clientHeight;
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    ctx.scale(DPR, DPR);

    const cols = 54;
    const rows = 16;
    let t = 0;
    let raf;

    const mouse = { x: -9999, y: -9999 };
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const sx = W / (cols - 1);
      const RADIUS = 130;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const nx = c / (cols - 1); // 0..1
          const x = c * sx;
          // central peak (mountain under the A)
          const center = Math.exp(-Math.pow((nx - 0.5) * 3.0, 2));
          const wave =
            Math.sin(nx * 11 + t * 1.7 + r * 0.4) * 7 +
            Math.sin(nx * 24 - t * 2.3 + r * 0.2) * 3.5;
          const baseY = H * 0.66 + r * (H * 0.028);
          let y = baseY - center * (H * 0.52) + wave - r * 1.6;

          // mouse repulsion + glow
          let boost = 0;
          const dx = x - mouse.x;
          const dy = y - mouse.y;
          const dist = Math.hypot(dx, dy);
          if (dist < RADIUS) {
            const f = 1 - dist / RADIUS;
            const ang = Math.atan2(dy, dx);
            const push = f * 26;
            // displace outward (x tracked visually via a small offset, y is what we draw)
            y += Math.sin(ang) * push;
            boost = f;
          }

          if (y > H + 6 || y < -6) continue;
          const drawX = x + (dist < RADIUS ? Math.cos(Math.atan2(dy, dx)) * (1 - dist / RADIUS) * 26 : 0);
          // green (#10B981) -> teal (#00E5FF)
          const R = Math.round(16 + (0 - 16) * nx);
          const G = Math.round(185 + (229 - 185) * nx);
          const B = Math.round(129 + (255 - 129) * nx);
          const alpha = Math.min(1, Math.max(0.06, 0.28 + center * 0.6 - r * 0.03 + boost * 0.6));
          const rad = 0.9 + center * 1.3 + boost * 1.8;
          ctx.beginPath();
          ctx.fillStyle = `rgba(${R},${G},${B},${alpha})`;
          ctx.arc(drawX, y, rad, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      t += 0.016;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, filter: "blur(10px)", scale: 1.03 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-0 z-[120] flex flex-col items-center justify-center overflow-hidden bg-arjuna-ink"
      data-testid="intro-overlay"
    >
      <div className="absolute inset-0 grid-lines opacity-20" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(60% 50% at 50% 45%, rgba(0,229,255,0.10), transparent 70%)" }}
      />

      {/* Rocket launch countdown. top right */}
      <div className="absolute right-5 top-5 sm:right-8 sm:top-8" data-testid="intro-countdown">
        <div className="glass flex items-center gap-3 rounded-full py-2 pl-4 pr-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">
            {count > 0 ? "Launching in" : "Liftoff"}
          </span>
          <span className="flex items-center gap-2">
            <motion.span
              key={count}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="font-mono text-lg font-semibold text-arjuna-teal"
            >
              {count > 0 ? `T-${count}` : "0"}
            </motion.span>
            <motion.span
              animate={count > 0 ? { y: [0, -2, 0] } : { y: -8, opacity: 0 }}
              transition={count > 0 ? { duration: 0.8, repeat: Infinity } : { duration: 0.6 }}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-arjuna-teal/15"
            >
              <Rocket className="h-4 w-4 -rotate-45 text-arjuna-teal" />
            </motion.span>
          </span>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative flex flex-col items-center"
      >
        <div className="relative h-[300px] w-[min(92vw,660px)]">
          <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
          {/* The rising "A" */}
          <div className="absolute left-1/2 top-[6%] h-[64%] -translate-x-1/2" style={{ aspectRatio: "1 / 1" }}>
            <svg viewBox="0 0 200 200" className="h-full w-full" preserveAspectRatio="xMidYMid meet">
              <g stroke="#ffffff" strokeWidth="3.4" fill="none" strokeLinecap="round" strokeLinejoin="round"
                 style={{ filter: "drop-shadow(0 0 10px rgba(0,229,255,0.5))" }}>
                <path d="M100 34 L58 158" />
                <path d="M100 34 L142 158" />
                <path d="M100 58 L74 142" />
                <path d="M100 58 L126 142" />
              </g>
            </svg>
          </div>
        </div>

        <div className="mt-2 font-heading text-2xl font-semibold tracking-[0.42em] text-white sm:text-3xl">
          ARJUNA <span className="text-arjuna-teal">SPACE</span>
        </div>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.32em] text-arjuna-teal sm:text-xs">
          Earth Intelligence. Amplified.
        </div>
      </motion.div>

      {/* 5s progress line */}
      <div className="absolute bottom-0 left-0 h-0.5 w-full bg-white/5">
        <motion.div
          className="h-full bg-gradient-to-r from-arjuna-agri to-arjuna-teal"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 5, ease: "linear" }}
        />
      </div>
    </motion.div>
  );
}
